import { Search, LogOut, User, Menu } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { ThemeToggle } from '@/components/theme-toggle'
import { HeaderTimer } from '@/components/timesheet/HeaderTimer'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
}

interface HeaderProps {
  onSearchClick?: () => void
  onMenuClick?: () => void
}

export function Header({ onSearchClick, onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth()

  return (
    <header className="glass-chrome mx-3 mt-3 flex h-12 shrink-0 items-center gap-2 rounded-2xl px-3">
      {user && (
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden -ml-2 shrink-0"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {user && (
        <Button
          variant="outline"
          size="sm"
          className="hidden w-56 items-center justify-start gap-2 rounded-full bg-card text-xs text-muted-foreground sm:flex"
          onClick={onSearchClick}
        >
          <Search className="h-3.5 w-3.5" />
          Search or jump to…
          <kbd className="ml-auto rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
        </Button>
      )}

      {user && <div className="ml-auto" />}

      {/* The running timer sits on every page, because a timer you have to
          navigate to is a timer that stays off. It renders nothing at all when
          nothing is running, so it costs no space elsewhere. */}
      {user && <HeaderTimer />}

      {user && <ThemeToggle />}

      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2" aria-label="Open user menu">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-sky-soft text-xs font-medium text-primary">
                  {getInitials(user.displayName)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm sm:block">{user.displayName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="font-normal">
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href="/settings" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Settings
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="flex items-center gap-2 text-destructive focus:text-destructive"
              onClick={() => logout()}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  )
}
